import { motion } from 'framer-motion'
import styled from 'styled-components'
import { AI_HELP_RESERVED_SPACE_CSS_VAR } from '@constants/layout'

const popVariants = {
  init: { scale: 1.1, y: 100 },
  show: { scale: 1, y: 0 },
  exit: { scale: 0.95, y: 0 }
}

// Tall forms paint at the pop-in offset, then the spring starts only after the
// heavy first render. That reads as the dialog jumping. Stay put instead.
const calmVariants = {
  init: { opacity: 1, scale: 1, y: 0 },
  show: { opacity: 1, scale: 1, y: 0 },
  exit: { opacity: 0, scale: 1, y: 0 }
}

export const ModalDialog = styled(motion.div).attrs<{ $calm?: boolean }>(({ $calm }) => ({
  variants: $calm ? calmVariants : popVariants,
  initial: 'init',
  animate: 'show',
  exit: 'exit',
  ...($calm ? { transition: { duration: 0.12, ease: 'easeOut' as const } } : {})
}))<{ width: number; height: number; $autoHeight?: boolean; $calm?: boolean }>`
  width: 100dvw;
  z-index: 50;
  border-radius: ${({ theme }) => theme.borderRadius.md}rem;
  width: ${({ width }) => width || 375}px;
  max-width: calc(100dvw - var(${AI_HELP_RESERVED_SPACE_CSS_VAR}, 0px));
  max-height: min(${({ height }) => height || 735}px, 100dvh);
  /* auto: content clips at max-height with no inner scroll. min(max-content, cap) keeps short
     modals compact while giving a definite height when content overflows. */
  height: ${({ $autoHeight, height }) => {
    const cap = height || 735
    if ($autoHeight) {
      return `min(max-content, min(${cap}px, 100dvh))`
    }
    return `min(100dvh, ${cap}px)`
  }};
  box-shadow: 0px 8px 22px rgba(27, 31, 35, 0.07);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: width 0.25s ease-in-out, max-height 0.25s ease-in-out;

  & > div {
    border-radius: ${({ theme }) => theme.borderRadius.md}rem;
    flex: 1 1 auto;
    min-height: 0;
    display: flex;
    flex-direction: column;
    overflow: hidden;
  }
`

export const ModalOverlay = styled(motion.div).attrs({
  // NOTE: About pointer events. Sometimes framer-motion animates exit
  // animation but does not remove the element from the DOM. This causes
  // the modal to be still clickable but not visible. To prevent this
  // we set pointer events to none when the modal is not visible.
  variants: {
    initial: { opacity: 0, pointerEvents: 'none' },
    isOpen: { opacity: 1, pointerEvents: 'all' },
    exit: { opacity: 0, transition: { duration: 0.2 }, pointerEvents: 'none' }
  },
  initial: 'initial',
  animate: 'isOpen',
  exit: 'exit',
  // Any onUpdate keeps the fade off WAAPI. A finished WAAPI fade is cancelled a frame before
  // framer-motion writes opacity 0, so the closing modal flashes at full opacity once.
  onUpdate: () => {}
})`
  position: fixed;
  top: 0;
  left: 0;
  right: var(${AI_HELP_RESERVED_SPACE_CSS_VAR}, 0px);
  bottom: 0;
  z-index: 9000;
  overflow-y: auto;
  background: ${({ theme }) => theme.colors.backdrop};
  display: flex;
  justify-content: center;
  align-items: center;

  @media only screen and (min-width: 376px) {
    overflow-y: auto;
  }
`
